import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, X, Copy, Check, Loader2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal for admin to create a "pure" partner — i.e. a partner who is NOT a conference
 * attendee. The server generates a temporary password and a referral link, both
 * displayed exactly once for the admin to share manually with the new partner.
 */
export default function CreatePartnerModal({ open, onClose, token, language = 'fr', onCreated }) {
  const t = language === 'fr' ? {
    title: 'Créer un Partenaire',
    subtitle: 'Crée un compte partenaire pur (sans inscription à la conférence).',
    first_name: 'Prénom', last_name: 'Nom', email: 'Courriel', phone: 'Téléphone (optionnel)',
    create: 'Créer le Partenaire', creating: 'Création…', cancel: 'Annuler', close: 'Fermer',
    successTitle: 'Partenaire créé avec succès',
    successHint: 'Copiez et envoyez ces identifiants au partenaire. Ils ne seront plus affichés.',
    tempPassword: 'Mot de passe temporaire', referralCode: 'Code de parrainage',
    referralLink: 'Lien de parrainage', loginUrl: 'URL de connexion',
    copied: 'Copié', copy: 'Copier',
  } : {
    title: 'Create Partner',
    subtitle: 'Create a pure partner account (no conference registration required).',
    first_name: 'First Name', last_name: 'Last Name', email: 'Email', phone: 'Phone (optional)',
    create: 'Create Partner', creating: 'Creating…', cancel: 'Cancel', close: 'Close',
    successTitle: 'Partner created successfully',
    successHint: 'Copy and send these credentials to the partner. They will not be shown again.',
    tempPassword: 'Temporary password', referralCode: 'Referral code',
    referralLink: 'Referral link', loginUrl: 'Login URL',
    copied: 'Copied', copy: 'Copy',
  };

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  if (!open) return null;

  const handleChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/partners`,
        { ...form, phone: form.phone || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      onCreated && onCreated(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create partner');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setForm({ first_name: '', last_name: '', email: '', phone: '' });
    setError('');
    onClose && onClose();
  };

  const copyValue = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch (_) {}
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={handleClose} data-testid="create-partner-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-extrabold text-[#1D1D1D] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" /> {t.title}
          </h3>
          <button onClick={handleClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!result ? (
          <form onSubmit={handleSubmit} className="p-5 space-y-3">
            <p className="text-xs text-gray-500">{t.subtitle}</p>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">{error}</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">{t.first_name}</label>
                <input data-testid="create-partner-first-name" required value={form.first_name} onChange={handleChange('first_name')}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">{t.last_name}</label>
                <input data-testid="create-partner-last-name" required value={form.last_name} onChange={handleChange('last_name')}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{t.email}</label>
              <input data-testid="create-partner-email" type="email" required value={form.email} onChange={handleChange('email')}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{t.phone}</label>
              <input data-testid="create-partner-phone" value={form.phone} onChange={handleChange('phone')}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div className="pt-2 flex justify-end gap-2">
              <button type="button" onClick={handleClose} className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded-lg text-sm">{t.cancel}</button>
              <button type="submit" disabled={saving} data-testid="create-partner-submit"
                className="bg-[#D4AF37] hover:bg-amber-500 text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {saving ? t.creating : t.create}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-5 space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 text-sm text-emerald-800">
              <p className="font-bold">{t.successTitle}</p>
              <p className="text-xs text-emerald-700 mt-0.5">{t.successHint}</p>
            </div>
            <Row label={t.email} value={result.user.email} k="email" onCopy={copyValue} copiedKey={copiedKey} t={t} />
            <Row label={t.tempPassword} value={result.temporary_password} k="pw" mono onCopy={copyValue} copiedKey={copiedKey} t={t} highlight />
            <Row label={t.referralCode} value={result.user.referral_code || ''} k="code" mono onCopy={copyValue} copiedKey={copiedKey} t={t} />
            <Row label={t.referralLink} value={result.referral_link} k="link" mono onCopy={copyValue} copiedKey={copiedKey} t={t} link />
            <Row label={t.loginUrl} value={result.login_url} k="login" mono onCopy={copyValue} copiedKey={copiedKey} t={t} link />
            <div className="pt-2 flex justify-end">
              <button onClick={handleClose} data-testid="create-partner-close-success"
                className="bg-[#1D1D1D] hover:bg-black text-white font-bold px-4 py-2 rounded-lg text-sm">{t.close}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, k, mono, onCopy, copiedKey, t, link, highlight }) {
  return (
    <div className={`rounded-xl px-3 py-2 border ${highlight ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{label}</p>
      <div className="flex items-center gap-2 mt-0.5">
        <code className={`flex-1 text-sm truncate ${mono ? 'font-mono' : ''} ${highlight ? 'font-bold text-[#1D1D1D]' : 'text-[#1D1D1D]'}`}>{value}</code>
        {link && (
          <a href={value} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:text-amber-600">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        <button onClick={() => onCopy(k, value)} className="text-xs font-bold text-[#D4AF37] hover:text-amber-600 flex items-center gap-1">
          {copiedKey === k ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
          {copiedKey === k ? t.copied : t.copy}
        </button>
      </div>
    </div>
  );
}
